import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { head, isNil } from "lodash";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import VideocamIcon from "@material-ui/icons/Videocam";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import ListIcon from "@material-ui/icons/List";
import Chip from "@material-ui/core/Chip";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  Typography,
  Paper,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import AddCircleOutline from "@material-ui/icons/AddCircleOutline";
import RemoveCircleOutline from "@material-ui/icons/RemoveCircleOutline";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../ConfirmationModal";
import MessageVariablesPicker from "../MessageVariablesPicker";
import UserStatusIcon from "../UserModal/statusIcon";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import { i18n } from "../../translate/i18n";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  variablesBox: {
    backgroundColor: "#e3f2fd",
    padding: theme.spacing(1.5),
    borderRadius: 8,
    marginBottom: theme.spacing(1),
  },
  variableChip: {
    margin: theme.spacing(0.25),
    cursor: "pointer",
    fontSize: "0.75rem",
    height: 24,
    "&:hover": {
      backgroundColor: "#1976d2",
      color: "white",
    },
  },
  messageField: {
    "& .MuiInputBase-root": {
      fontFamily: "monospace",
      fontSize: "0.9rem",
    },
  },
  // Estilos do celular
  phoneContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: theme.spacing(2),
  },
  phoneFrame: {
    width: 280,
    height: 560,
    backgroundColor: "#1a1a1a",
    borderRadius: 36,
    padding: "8px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
    position: "relative",
  },
  phoneScreen: {
    width: "100%",
    height: "100%",
    backgroundColor: "#0b141a",
    borderRadius: 28,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  phoneHeader: {
    backgroundColor: "#1f2c34",
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  phoneAvatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: "#00a884",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontWeight: "bold",
    fontSize: 16,
  },
  phoneHeaderInfo: {
    flex: 1,
  },
  phoneHeaderName: {
    color: "#e9edef",
    fontSize: 14,
    fontWeight: 500,
  },
  phoneHeaderStatus: {
    color: "#8696a0",
    fontSize: 12,
  },
  phoneChat: {
    flex: 1,
    padding: "16px",
    overflowY: "auto",
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23182229' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    backgroundColor: "#0b141a",
  },
  messageBubble: {
    backgroundColor: "#005c4b",
    borderRadius: "8px 8px 0 8px",
    padding: "8px 12px",
    maxWidth: "85%",
    marginLeft: "auto",
    marginBottom: 8,
    position: "relative",
  },
  messageText: {
    color: "#e9edef",
    fontSize: 13,
    lineHeight: 1.4,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  messageTime: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 4,
    marginTop: 4,
  },
  messageTimeText: {
    color: "#8696a0",
    fontSize: 11,
  },
  messageCheck: {
    color: "#53bdeb",
    fontSize: 16,
  },
  mediaPreview: {
    borderRadius: 6,
    marginBottom: 8,
    maxWidth: "100%",
    backgroundColor: "#1f2c34",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  mediaImage: {
    maxWidth: "100%",
    maxHeight: 180,
    borderRadius: 6,
  },
  mediaPlaceholder: {
    width: "100%",
    height: 150,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#8696a0",
    gap: 8,
  },
  phoneNotch: {
    position: "absolute",
    top: 8,
    left: "50%",
    transform: "translateX(-50%)",
    width: 80,
    height: 24,
    backgroundColor: "#1a1a1a",
    borderRadius: 12,
    zIndex: 10,
  },
  emptyMessage: {
    textAlign: "center",
    color: "#8696a0",
    padding: 20,
    fontSize: 13,
  },
  previewTitle: {
    textAlign: "center",
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  interactivePreviewBubble: {
    marginTop: 6,
    padding: "10px 12px",
    borderRadius: 8,
    backgroundColor: "#005c4b",
    maxWidth: "85%",
    alignSelf: "flex-end",
  },
  interactiveButtonsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 8,
  },
  interactiveButtonPill: {
    padding: "8px 14px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.5)",
    backgroundColor: "transparent",
    color: "#e9edef",
    fontSize: 13,
  },
  interactiveListButton: {
    marginTop: 8,
    padding: "10px 14px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.5)",
    backgroundColor: "transparent",
    color: "#53bdeb",
    fontSize: 13,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  interactiveListRows: {
    marginTop: 4,
    borderTop: "1px solid rgba(255,255,255,0.15)",
    paddingTop: 6,
  },
  interactiveListRow: {
    padding: "6px 0",
    fontSize: 13,
    color: "#e9edef",
  },
  // Linha cortada pelo texto (mesmo padrão de outros campos)
  dividerWithText: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  dividerLine: {
    flex: 1,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  dividerText: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    flexShrink: 0,
  },
}));

const CampaignProSchema = Yup.object().shape({
  name: Yup.string().min(2, "Muito curto!").max(50, "Muito longo!").required("Obrigatório"),
  contactListProId: Yup.mixed().nullable(),
  tagIds: Yup.array().of(Yup.number()).nullable(),
  scheduledAt: Yup.string().required("Informe a data e hora do agendamento"),
  message1: Yup.string(),
  message2: Yup.string(),
  message3: Yup.string(),
  message4: Yup.string(),
  message5: Yup.string(),
})
  .test("listOrTags", "Selecione uma Lista de Contatos PRO ou uma ou mais Tags", function (value) {
    const hasList = value?.contactListProId != null && value?.contactListProId !== "";
    const hasTags = Array.isArray(value?.tagIds) && value.tagIds.length > 0;
    if (hasList || hasTags) return true;
    return this.createError({
      path: "contactListProId",
      message: "Selecione uma Lista de Contatos PRO ou uma ou mais Tags"
    });
  })
  .test("atLeastOneMessage", "Preencha pelo menos uma mensagem (Msg. 1 a 5)", function (value) {
    const messages = [
      value?.message1,
      value?.message2,
      value?.message3,
      value?.message4,
      value?.message5,
    ].filter(Boolean);
    const hasContent = messages.some((m) => String(m).trim().length > 0);
    return hasContent;
  });

// Variáveis disponíveis para usar nas mensagens
const AVAILABLE_VARIABLES = [
  { key: "{nome}", label: "nome", example: "João Silva" },
  { key: "{telefone}", label: "telefone", example: "5511999999999" },
  { key: "{var1}", label: "var1", example: "Produto X" },
  { key: "{var2}", label: "var2", example: "R$ 99,90" },
  { key: "{var3}", label: "var3", example: "10/12/2025" },
  { key: "{var4}", label: "var4", example: "Premium" },
  { key: "{var5}", label: "var5", example: "ABC123" },
  { key: "{var6}", label: "var6", example: "XYZ789" },
];

// Componente de Preview do Celular - Múltiplas Mídias
const PhonePreview = ({ 
  message, 
  attachment, mediaPath, mediaName,
  attachment2, mediaPath2, mediaName2,
  attachment3, mediaPath3, mediaName3,
  sendInteractive,
  interactiveType,
  interactiveBodyText,
  interactiveButtonText,
  interactiveButtons,
  interactiveSections,
}) => {
  const classes = useStyles();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewUrl2, setPreviewUrl2] = useState(null);
  const [previewUrl3, setPreviewUrl3] = useState(null);

  // Substituir variáveis por exemplos
  const processedMessage = useMemo(() => {
    if (!message) return "";
    let processed = message;
    AVAILABLE_VARIABLES.forEach((v) => {
      processed = processed.replace(new RegExp(v.key.replace(/[{}]/g, "\\$&"), "g"), v.example);
    });
    return processed;
  }, [message]);

  // Gerar preview do anexo 1
  useEffect(() => {
    if (attachment) {
      const url = URL.createObjectURL(attachment);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else if (mediaPath) {
      setPreviewUrl(mediaPath);
    } else {
      setPreviewUrl(null);
    }
  }, [attachment, mediaPath]);

  // Gerar preview do anexo 2
  useEffect(() => {
    if (attachment2) {
      const url = URL.createObjectURL(attachment2);
      setPreviewUrl2(url);
      return () => URL.revokeObjectURL(url);
    } else if (mediaPath2) {
      setPreviewUrl2(mediaPath2);
    } else {
      setPreviewUrl2(null);
    }
  }, [attachment2, mediaPath2]);

  // Gerar preview do anexo 3
  useEffect(() => {
    if (attachment3) {
      const url = URL.createObjectURL(attachment3);
      setPreviewUrl3(url);
      return () => URL.revokeObjectURL(url);
    } else if (mediaPath3) {
      setPreviewUrl3(mediaPath3);
    } else {
      setPreviewUrl3(null);
    }
  }, [attachment3, mediaPath3]);

  const getFileType = (att, mName) => {
    const name = att?.name || mName || "";
    const ext = name.split(".").pop()?.toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "image";
    if (["mp4", "mov", "avi", "webm"].includes(ext)) return "video";
    return "file";
  };

  const renderSingleMedia = (url, att, mName, label) => {
    if (!url && !att) return null;
    
    const fileType = getFileType(att, mName);
    
    if (fileType === "image" && url) {
      return (
        <Box className={classes.mediaPreview} style={{ marginBottom: 4 }}>
          <img src={url} alt={label} className={classes.mediaImage} />
        </Box>
      );
    }
    
    if (fileType === "video") {
      return (
        <Box className={classes.mediaPreview} style={{ marginBottom: 4 }}>
          <Box className={classes.mediaPlaceholder}>
            <VideocamIcon style={{ fontSize: 32 }} />
            <Typography variant="caption" style={{ fontSize: 10 }}>{att?.name || mName}</Typography>
          </Box>
        </Box>
      );
    }
    
    return (
      <Box className={classes.mediaPreview} style={{ marginBottom: 4 }}>
        <Box className={classes.mediaPlaceholder}>
          <InsertDriveFileIcon style={{ fontSize: 32 }} />
          <Typography variant="caption" style={{ fontSize: 10 }}>{att?.name || mName}</Typography>
        </Box>
      </Box>
    );
  };

  const hasMedia1 = previewUrl || attachment;
  const hasMedia2 = previewUrl2 || attachment2;
  const hasMedia3 = previewUrl3 || attachment3;
  const hasInteractiveBlock = !!(
    sendInteractive &&
    (interactiveType === "button"
      ? Array.isArray(interactiveButtons) && interactiveButtons.some((b) => b && String(b.title || "").trim())
      : interactiveType === "list" &&
        Array.isArray(interactiveSections) &&
        interactiveSections.some((s) => s && Array.isArray(s.rows) && s.rows.some((r) => r && String(r.title || "").trim()))
    )
  );
  const hasContent = processedMessage || hasMedia1 || hasMedia2 || hasMedia3 || hasInteractiveBlock;

  return (
    <Box className={classes.phoneContainer}>
      <Box>
        <Typography className={classes.previewTitle}>
          📱 Preview em tempo real
        </Typography>
        <Box className={classes.phoneFrame}>
          <Box className={classes.phoneNotch} />
          <Box className={classes.phoneScreen}>
            {/* Header */}
            <Box className={classes.phoneHeader}>
              <Box className={classes.phoneAvatar}>
                <span>J</span>
              </Box>
              <Box className={classes.phoneHeaderInfo}>
                <Typography className={classes.phoneHeaderName}>
                  João Silva
                </Typography>
                <Typography className={classes.phoneHeaderStatus}>
                  online
                </Typography>
              </Box>
            </Box>

            {/* Chat */}
            <Box className={classes.phoneChat}>
              {hasContent ? (
                <>
                  {/* Mensagem de Texto */}
                  {processedMessage && (
                    <Box className={classes.messageBubble} style={{ marginBottom: 6 }}>
                      <Typography className={classes.messageText}>
                        {processedMessage}
                      </Typography>
                      <Box className={classes.messageTime}>
                        <Typography className={classes.messageTimeText}>
                          {moment().format("HH:mm")}
                        </Typography>
                        <DoneAllIcon className={classes.messageCheck} />
                      </Box>
                    </Box>
                  )}
                  
                  {/* Mídia 1 */}
                  {hasMedia1 && (
                    <Box className={classes.messageBubble} style={{ marginBottom: 6, padding: 4 }}>
                      {renderSingleMedia(previewUrl, attachment, mediaName, "Arquivo 1")}
                      <Box className={classes.messageTime}>
                        <Typography className={classes.messageTimeText}>
                          {moment().format("HH:mm")}
                        </Typography>
                        <DoneAllIcon className={classes.messageCheck} />
                      </Box>
                    </Box>
                  )}

                  {/* Mídia 2 */}
                  {hasMedia2 && (
                    <Box className={classes.messageBubble} style={{ marginBottom: 6, padding: 4 }}>
                      {renderSingleMedia(previewUrl2, attachment2, mediaName2, "Arquivo 2")}
                      <Box className={classes.messageTime}>
                        <Typography className={classes.messageTimeText}>
                          {moment().format("HH:mm")}
                        </Typography>
                        <DoneAllIcon className={classes.messageCheck} />
                      </Box>
                    </Box>
                  )}

                  {/* Mídia 3 */}
                  {hasMedia3 && (
                    <Box className={classes.messageBubble} style={{ marginBottom: 6, padding: 4 }}>
                      {renderSingleMedia(previewUrl3, attachment3, mediaName3, "Arquivo 3")}
                      <Box className={classes.messageTime}>
                        <Typography className={classes.messageTimeText}>
                          {moment().format("HH:mm")}
                        </Typography>
                        <DoneAllIcon className={classes.messageCheck} />
                      </Box>
                    </Box>
                  )}

                  {/* Botões ou Lista (interativo) */}
                  {hasInteractiveBlock && (
                    <Box className={classes.messageBubble} style={{ marginBottom: 6, padding: "10px 12px" }}>
                      {(interactiveBodyText && String(interactiveBodyText).trim()) ? (
                        <Typography className={classes.messageText} style={{ marginBottom: 8 }}>
                          {String(interactiveBodyText).trim()}
                        </Typography>
                      ) : null}
                      {interactiveType === "button" && (
                        <Box className={classes.interactiveButtonsRow}>
                          {(interactiveButtons || [])
                            .filter((b) => b && String(b.title || "").trim())
                            .slice(0, 3)
                            .map((btn, i) => (
                              <span key={btn.id || i} className={classes.interactiveButtonPill}>
                                {String(btn.title).trim()}
                              </span>
                            ))}
                        </Box>
                      )}
                      {interactiveType === "list" && (
                        <>
                          <Box className={classes.interactiveListButton}>
                            <ListIcon style={{ fontSize: 18 }} />
                            {String(interactiveButtonText || "Ver opções").trim()}
                          </Box>
                          {(interactiveSections || []).map((sec, si) => (
                            <Box key={si} className={classes.interactiveListRows}>
                              {sec.title ? (
                                <Typography variant="caption" style={{ color: "#8696a0", marginBottom: 4 }}>
                                  {sec.title}
                                </Typography>
                              ) : null}
                              {(sec.rows || []).filter((r) => r && String(r.title || "").trim()).map((row, ri) => (
                                <Box key={row.id || ri} className={classes.interactiveListRow}>
                                  {String(row.title).trim()}
                                </Box>
                              ))}
                            </Box>
                          ))}
                        </>
                      )}
                      <Box className={classes.messageTime}>
                        <Typography className={classes.messageTimeText}>
                          {moment().format("HH:mm")}
                        </Typography>
                        <DoneAllIcon className={classes.messageCheck} />
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Typography className={classes.emptyMessage}>
                  Digite sua mensagem para ver o preview
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const filterOptions = createFilterOptions({
  trim: true,
});

const CampaignProModal = ({ open, onClose, campaignId, duplicateFromId, onSave, resetPagination, initialContactListProId }) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);
  const { companyId } = user;

  const initialState = {
    name: "",
    message1: "",
    message2: "",
    message3: "",
    message4: "",
    message5: "",
    confirmationMessage1: "",
    confirmationMessage2: "",
    confirmationMessage3: "",
    confirmationMessage4: "",
    confirmationMessage5: "",
    status: "INATIVA",
    confirmation: false,
    scheduledAt: "",
    contactListProId: "",
    tagIds: [],
    companyId,
    statusTicket: "closed",
    openTicket: "disabled",
    ghostMode: false,
    dispatchDays: ["monday", "tuesday", "wednesday", "thursday", "friday"],
    dispatchStartTime: "09:00",
    dispatchEndTime: "18:00",
    pauseOutsideHours: true,
    whatsappIds: [],
    sendInteractive: false,
    interactiveType: "button",
    interactiveBodyText: "Escolha uma opção:",
    interactiveButtonText: "Ver opções",
    interactiveButtons: [{ id: "opt1", title: "Opção 1" }, { id: "opt2", title: "Opção 2" }],
    interactiveSections: [
      { title: "Seção 1", rows: [{ id: "row1", title: "Item 1", description: "" }, { id: "row2", title: "Item 2", description: "" }] },
    ],
  };

  const [campaign, setCampaign] = useState(initialState);
  const [whatsapps, setWhatsapps] = useState([]);
  const [whatsappId, setWhatsappId] = useState(null);
  const [whatsappIds, setWhatsappIds] = useState([]);
  const [contactLists, setContactLists] = useState([]);
  const [tags, setTags] = useState([]);
  const [flows, setFlows] = useState([]);
  const [messageTab, setMessageTab] = useState(0);
  const [attachment, setAttachment] = useState(null);
  const [attachment2, setAttachment2] = useState(null);
  const [attachment3, setAttachment3] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [deleteMediaIndex, setDeleteMediaIndex] = useState(null);
  const [campaignEditable, setCampaignEditable] = useState(true);
  const attachmentFile = useRef(null);
  const attachmentFile2 = useRef(null);
  const attachmentFile3 = useRef(null);

  // Estados para usuários e filas
  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const { findAll: findAllQueues } = useQueues();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Carregar filas
  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        const list = await findAllQueues();
        setAllQueues(list);
        setQueues(list);
      };
      loadQueues();
    }
  }, []);

  // Buscar usuários
  useEffect(() => {
    if (searchParam.length < 3) {
      setLoading(false);
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", { params: { showAll: true } });
          setOptions(data.users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  useEffect(() => {
    if (isMounted.current && open) {
      api.get(`/contact-lists-pro/list`, { params: { companyId } })
        .then(({ data }) => setContactLists(data));

      api.get("/tags/list", { params: { companyId, kanban: 0 } })
        .then(({ data }) => setTags(Array.isArray(data) ? data : []))
        .catch(() => setTags([]));

      api.get("/flowbuilder")
        .then((res) => {
          const list = res.data?.flows || [];
          setFlows(Array.isArray(list) ? list.filter((f) => f.active) : []);
        })
        .catch(() => setFlows([]));

      api.get(`/whatsapp`, { params: { companyId, session: 0 } })
        .then(({ data }) => {
          // Filtrar conexões válidas para disparo de campanhas
          // Excluir: API Oficial (waba_id preenchido), Facebook e Instagram
          const validWhatsapps = data.filter(w => 
            !w.waba_id && // Não é API Oficial (waba_id vazio)
            !w.phone_number_id && // Não é API Oficial (phone_number_id vazio)
            w.type !== "facebook" &&
            w.type !== "instagram" &&
            w.channel !== "facebook" &&
            w.channel !== "instagram"
          );
          setWhatsapps(validWhatsapps);
        });

      const idToLoad = campaignId || duplicateFromId;
      if (!idToLoad) return;

      api.get(`/campaigns-pro/${idToLoad}`).then(({ data }) => {
        if (data?.whatsappId) {
          setWhatsappId(parseInt(data.whatsappId));
        }

        setCampaign((prev) => {
          let prevData = { ...prev };
          Object.entries(data).forEach(([key, value]) => {
            if (key === "scheduledAt") {
              prevData[key] = duplicateFromId ? "" : (value ? moment(value).format("YYYY-MM-DDTHH:mm") : prevData[key]);
            } else if (key === "name" && duplicateFromId) {
              prevData[key] = `Cópia de ${data.name || "Campanha"}`;
            } else if (key === "tagIds") {
              prevData[key] = Array.isArray(value) ? value : value ? [].concat(value) : [];
            } else if (key === "dispatchDays") {
              prevData[key] = Array.isArray(value) ? value : value ? (typeof value === "string" ? JSON.parse(value || "[]") : []) : [];
            } else if (key === "whatsappIds") {
              const parsed = Array.isArray(value) ? value : value ? (typeof value === "string" ? JSON.parse(value || "[]") : [].concat(value)) : [];
              prevData[key] = parsed;
            } else if (key === "interactiveButtons" || key === "interactiveSections") {
              try {
                prevData[key] = value == null ? (key === "interactiveButtons" ? [{ id: "opt1", title: "Opção 1" }, { id: "opt2", title: "Opção 2" }] : [{ title: "Seção 1", rows: [{ id: "row1", title: "Item 1", description: "" }] }]) : (typeof value === "string" ? JSON.parse(value || "[]") : value);
              } catch {
                prevData[key] = key === "interactiveButtons" ? [{ id: "opt1", title: "Opção 1" }] : [{ title: "Seção 1", rows: [] }];
              }
            } else {
              prevData[key] = value === null ? "" : value;
            }
          });
          if (duplicateFromId) {
            delete prevData.id;
            prevData.status = "INATIVA";
          }
          return prevData;
        });
        const ids = data.whatsappIds;
        if (ids != null) {
          const parsed = Array.isArray(ids) ? ids : (typeof ids === "string" ? JSON.parse(ids || "[]") : []);
          setWhatsappIds(parsed);
          if (parsed.length > 0 && !data.whatsappId) setWhatsappId(parseInt(parsed[0], 10));
        }

        // Preencher Usuário e Setor (Fila) ao editar/visualizar campanha
        if (data.user) {
          setSelectedUser(data.user);
          if (Array.isArray(data.user.queues) && data.user.queues.length > 0) {
            setQueues(data.user.queues);
          }
        } else {
          setSelectedUser(null);
        }
        const queueId = data.queueId ?? data.queue?.id ?? "";
        setSelectedQueue(queueId !== "" && queueId != null ? queueId : "");
      });
    }
  }, [campaignId, duplicateFromId, open, companyId]);

  useEffect(() => {
    if (open && initialContactListProId) {
      setCampaign((prev) => ({ ...prev, contactListProId: initialContactListProId }));
    }
  }, [open, initialContactListProId]);

  useEffect(() => {
    const now = moment();
    const scheduledAt = moment(campaign.scheduledAt);
    const moreThanAnHour = !Number.isNaN(scheduledAt.diff(now)) && scheduledAt.diff(now, "hour") > 1;
    const isEditable = campaign.status === "INATIVA" || (campaign.status === "PROGRAMADA" && moreThanAnHour);
    setCampaignEditable(isEditable);
  }, [campaign.status, campaign.scheduledAt]);

  const handleClose = () => {
    onClose();
    setCampaign(initialState);
    setAttachment(null);
    setWhatsappId(null);
    setSelectedUser(null);
    setSelectedQueue(null);
    setQueues(allQueues);
    setSearchParam("");
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const handleSaveCampaign = async (values) => {
    try {
      // Validar whatsappId
      if (!whatsappId) {
        toast.error("Selecione uma conexão WhatsApp");
        return;
      }
      if (values.sendInteractive && values.openTicket !== "enabled") {
        toast.error("Envio de Botões/Lista exige que 'Abrir ticket' esteja habilitado.");
        return;
      }
      if (values.sendInteractive && values.interactiveType === "button" && (!values.interactiveButtons || values.interactiveButtons.length === 0)) {
        toast.error("Preencha pelo menos um botão (ID e Título).");
        return;
      }
      if (values.sendInteractive && values.interactiveType === "list" && (!values.interactiveSections || values.interactiveSections.length === 0)) {
        toast.error("Preencha pelo menos uma seção com itens na lista.");
        return;
      }

      const effectiveWhatsappId = whatsappId || (whatsappIds && whatsappIds[0]);
      const dataValues = {
        ...values,
        whatsappId: effectiveWhatsappId,
        userId: selectedUser?.id || null,
        queueId: selectedQueue || null,
        statusTicket: values.statusTicket || "closed",
        dispatchDays: Array.isArray(values.dispatchDays) ? JSON.stringify(values.dispatchDays) : (values.dispatchDays || null),
        whatsappIds: whatsappIds && whatsappIds.length > 0 ? JSON.stringify(whatsappIds) : null,
      };

      Object.entries(values).forEach(([key, value]) => {
        if (key === "scheduledAt" && value) {
          dataValues[key] = moment(value).format("YYYY-MM-DD HH:mm:ss");
        } else if (key !== "dispatchDays" && key !== "whatsappIds" && key !== "userId" && key !== "queueId") {
          dataValues[key] = value === "" ? null : value;
        }
      });
      dataValues.userId = selectedUser?.id ?? dataValues.userId ?? null;
      dataValues.queueId = selectedQueue ?? dataValues.queueId ?? null;

      if (campaignId) {
        await api.put(`/campaigns-pro/${campaignId}`, dataValues);
        // Upload de mídias
        if (attachment) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/campaigns-pro/${campaignId}/media-upload?mediaIndex=1`, formData);
        }
        if (attachment2) {
          const formData2 = new FormData();
          formData2.append("file", attachment2);
          await api.post(`/campaigns-pro/${campaignId}/media-upload?mediaIndex=2`, formData2);
        }
        if (attachment3) {
          const formData3 = new FormData();
          formData3.append("file", attachment3);
          await api.post(`/campaigns-pro/${campaignId}/media-upload?mediaIndex=3`, formData3);
        }
        handleClose();
      } else {
        delete dataValues.id; // criação/duplicação: não enviar id
        const { data } = await api.post("/campaigns-pro", dataValues);
        // Upload de mídias
        if (attachment) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/campaigns-pro/${data.id}/media-upload?mediaIndex=1`, formData);
        }
        if (attachment2) {
          const formData2 = new FormData();
          formData2.append("file", attachment2);
          await api.post(`/campaigns-pro/${data.id}/media-upload?mediaIndex=2`, formData2);
        }
        if (attachment3) {
          const formData3 = new FormData();
          formData3.append("file", attachment3);
          await api.post(`/campaigns-pro/${data.id}/media-upload?mediaIndex=3`, formData3);
        }
        if (onSave) onSave(data);
        handleClose();
      }
      toast.success("Campanha salva com sucesso!");
      if (resetPagination) resetPagination();
    } catch (err) {
      console.log(err);
      toastError(err);
    }
  };

  const deleteMedia = async (mediaIndex = 1) => {
    if (mediaIndex === 1) {
      if (attachment) {
        setAttachment(null);
        if (attachmentFile.current) attachmentFile.current.value = null;
      }
      if (campaign.mediaPath) {
        await api.delete(`/campaigns-pro/${campaign.id}/media-upload?mediaIndex=1`);
        setCampaign((prev) => ({ ...prev, mediaPath: null, mediaName: null }));
        toast.success("Arquivo 1 removido!");
      }
    } else if (mediaIndex === 2) {
      if (attachment2) {
        setAttachment2(null);
        if (attachmentFile2.current) attachmentFile2.current.value = null;
      }
      if (campaign.mediaPath2) {
        await api.delete(`/campaigns-pro/${campaign.id}/media-upload?mediaIndex=2`);
        setCampaign((prev) => ({ ...prev, mediaPath2: null, mediaName2: null }));
        toast.success("Arquivo 2 removido!");
      }
    } else if (mediaIndex === 3) {
      if (attachment3) {
        setAttachment3(null);
        if (attachmentFile3.current) attachmentFile3.current.value = null;
      }
      if (campaign.mediaPath3) {
        await api.delete(`/campaigns-pro/${campaign.id}/media-upload?mediaIndex=3`);
        setCampaign((prev) => ({ ...prev, mediaPath3: null, mediaName3: null }));
        toast.success("Arquivo 3 removido!");
      }
    }
    setDeleteMediaIndex(null);
    setConfirmationOpen(false);
  };

  const handleDeleteMediaClick = (index) => {
    setDeleteMediaIndex(index);
    setConfirmationOpen(true);
  };

  const insertVariable = (variable, setFieldValue, fieldName) => {
    const field = document.querySelector(`textarea[name="${fieldName}"]`);
    if (field) {
      const start = field.selectionStart;
      const end = field.selectionEnd;
      const text = field.value;
      const newText = text.substring(0, start) + variable + text.substring(end);
      setFieldValue(fieldName, newText);
      
      setTimeout(() => {
        field.focus();
        field.setSelectionRange(start + variable.length, start + variable.length);
      }, 10);
    }
  };

  const renderVariablesHelper = (setFieldValue, fieldName) => (
    <>
      <Paper className={classes.variablesBox} elevation={0}>
        <Typography variant="caption" gutterBottom style={{ color: "#1565c0", fontWeight: "bold" }}>
          Variáveis da lista (clique para inserir):
        </Typography>
        <Box display="flex" flexWrap="wrap" mt={0.5}>
          {AVAILABLE_VARIABLES.map((v) => (
            <Chip
              key={v.key}
              label={v.key}
              size="small"
              color="primary"
              className={classes.variableChip}
              onClick={() => insertVariable(v.key, setFieldValue, fieldName)}
            />
          ))}
        </Box>
      </Paper>
      <Box mt={1}>
        <MessageVariablesPicker
          onClick={(value) => insertVariable(value, setFieldValue, fieldName)}
          disabled={!campaignEditable && campaign.status !== "CANCELADA"}
          showSchedulingVars={false}
        />
      </Box>
    </>
  );

  const renderMessageField = (identifier, setFieldValue) => (
    <>
      {renderVariablesHelper(setFieldValue, identifier)}
      <Field
        as={TextField}
        id={identifier}
        name={identifier}
        fullWidth
        rows={4}
        label={`Mensagem ${identifier.replace("message", "")}`}
        placeholder="Olá {nome}! Sua variável é {var1}..."
        multiline
        variant="outlined"
        disabled={!campaignEditable && campaign.status !== "CANCELADA"}
        className={classes.messageField}
        helperText="Use as variáveis acima para personalizar sua mensagem"
      />
    </>
  );

  const cancelCampaign = async () => {
    try {
      await api.post(`/campaigns-pro/${campaign.id}/cancel`);
      toast.success("Campanha cancelada!");
      setCampaign((prev) => ({ ...prev, status: "CANCELADA" }));
      if (resetPagination) resetPagination();
    } catch (err) {
      toastError(err);
    }
  };

  const restartCampaign = async () => {
    try {
      await api.post(`/campaigns-pro/${campaign.id}/restart`);
      toast.success("Campanha reiniciada!");
      setCampaign((prev) => ({ ...prev, status: "EM_ANDAMENTO" }));
      if (resetPagination) resetPagination();
    } catch (err) {
      toastError(err);
    }
  };

  const getCurrentMessage = (values) => {
    const messages = [values.message1, values.message2, values.message3, values.message4, values.message5];
    return messages[messageTab] || "";
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title="Remover mídia"
        open={confirmationOpen}
        onClose={() => {
          setConfirmationOpen(false);
          setDeleteMediaIndex(null);
        }}
        onConfirm={() => deleteMedia(deleteMediaIndex || 1)}
      >
        Tem certeza que deseja remover o arquivo {deleteMediaIndex || 1}?
      </ConfirmationModal>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl" scroll="paper">
        <DialogTitle>
          {campaignEditable ? (
            <>{duplicateFromId ? "Duplicar Campanha PRO" : campaignId ? "Editar Campanha PRO" : "Nova Campanha PRO"}</>
          ) : (
            <>Visualizar Campanha PRO</>
          )}
        </DialogTitle>

        <div style={{ display: "none" }}>
          <input
            type="file"
            ref={attachmentFile}
            onChange={handleAttachmentFile}
          />
        </div>

        <Formik
          initialValues={campaign}
          enableReinitialize
          validationSchema={CampaignProSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveCampaign(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Grid spacing={3} container>
                  {/* Coluna do Formulário */}
                  <Grid item xs={12} md={8}>
                    <Grid spacing={2} container>
                      {/* Nome da Campanha */}
                      <Grid xs={12} md={6} item>
                        <Field
                          as={TextField}
                          label="Nome da Campanha *"
                          name="name"
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          disabled={!campaignEditable}
                        />
                      </Grid>

                      {/* Lista de Contatos PRO - com campo de pesquisa */}
                      <Grid xs={12} md={6} item>
                        <Autocomplete
                          value={contactLists.find((l) => l.id === values.contactListProId) || null}
                          onChange={(e, newValue) => {
                            setFieldValue("contactListProId", newValue ? newValue.id : "");
                          }}
                          getOptionLabel={(option) =>
                            option && option.name
                              ? `Lista PRO: ${option.name} (${option.contactsCount ?? 0})`
                              : ""
                          }
                          options={contactLists}
                          filterOptions={filterOptions}
                          disabled={!campaignEditable}
                          fullWidth
                          size="small"
                          noOptionsText="Nenhuma lista encontrada"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Lista de Contatos PRO"
                              variant="outlined"
                              margin="dense"
                              placeholder="Pesquisar lista..."
                              error={touched.contactListProId && Boolean(errors.contactListProId)}
                              helperText={touched.contactListProId && errors.contactListProId}
                            />
                          )}
                        />
                      </Grid>

                      {/* Tag(s) - uma ou mais tags */}
                      <Grid xs={12} md={6} item>
                        <FormControl
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          error={touched.tagIds && Boolean(errors.tagIds)}
                        >
                          <InputLabel id="campaign-pro-tags-label">Tag(s)</InputLabel>
                          <Select
                            labelId="campaign-pro-tags-label"
                            multiple
                            value={values.tagIds || []}
                            onChange={(e) => setFieldValue("tagIds", e.target.value)}
                            label="Tag(s)"
                            disabled={!campaignEditable}
                            renderValue={(selected) => (
                              <Box display="flex" flexWrap="wrap" gap={8}>
                                {(selected || []).map((value) => {
                                  const tag = tags.find((t) => t.id === value);
                                  return (
                                    <Chip
                                      key={value}
                                      label={tag?.name || value}
                                      size="small"
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          >
                            {tags.map((tag) => (
                              <MenuItem key={tag.id} value={tag.id}>
                                {tag.name}
                              </MenuItem>
                            ))}
                          </Select>
                          <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
                            Selecione lista de contatos OU uma ou mais tags. Contatos que possuírem qualquer uma das tags receberão a campanha.
                          </Typography>
                        </FormControl>
                      </Grid>

                      {/* Dias da Semana para Disparo */}
                      <Grid xs={12} item>
                        <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                          Dias da semana para disparo:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {[
                            { value: "monday", label: "Segunda" },
                            { value: "tuesday", label: "Terça" },
                            { value: "wednesday", label: "Quarta" },
                            { value: "thursday", label: "Quinta" },
                            { value: "friday", label: "Sexta" },
                            { value: "saturday", label: "Sábado" },
                            { value: "sunday", label: "Domingo" },
                          ].map((day) => {
                            const isSelected = Array.isArray(values.dispatchDays) && values.dispatchDays.includes(day.value);
                            return (
                              <Chip
                                key={day.value}
                                label={day.label}
                                onClick={() => {
                                  if (!campaignEditable) return;
                                  const current = Array.isArray(values.dispatchDays) ? [...values.dispatchDays] : [];
                                  if (isSelected) {
                                    const idx = current.indexOf(day.value);
                                    if (idx > -1) current.splice(idx, 1);
                                  } else {
                                    current.push(day.value);
                                  }
                                  setFieldValue("dispatchDays", current);
                                }}
                                color={isSelected ? "primary" : "default"}
                                variant={isSelected ? "default" : "outlined"}
                                style={{ cursor: campaignEditable ? "pointer" : "default" }}
                              />
                            );
                          })}
                        </Box>
                      </Grid>

                      {/* Horário de início e fim */}
                      <Grid xs={12} md={6} item>
                        <Field
                          as={TextField}
                          label="Horário de início"
                          name="dispatchStartTime"
                          type="time"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          disabled={!campaignEditable}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid xs={12} md={6} item>
                        <Field
                          as={TextField}
                          label="Horário de fim"
                          name="dispatchEndTime"
                          type="time"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          disabled={!campaignEditable}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      {/* Pausar fora do horário */}
                      <Grid xs={12} item>
                        <FormControlLabel
                          control={
                            <Field
                              as={Checkbox}
                              color="primary"
                              name="pauseOutsideHours"
                              type="checkbox"
                            />
                          }
                          label="Pausar automaticamente fora do horário e retomar no próximo horário útil"
                          disabled={!campaignEditable}
                        />
                        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
                          Quando ativado, a campanha pausa ao sair do horário configurado e retoma no próximo dia/horário permitido.
                        </Typography>
                      </Grid>

                      {/* Conexão WhatsApp / WhatsApps para Randomização */}
                      <Grid xs={12} md={6} item>
                        <FormControl variant="outlined" margin="dense" fullWidth>
                          <InputLabel>WhatsApp(s) para Randomização *</InputLabel>
                          <Select
                            multiple
                            label="WhatsApp(s) para Randomização *"
                            value={whatsappIds.length > 0 ? whatsappIds : (whatsappId ? [whatsappId] : [])}
                            onChange={(e) => {
                              const selected = e.target.value;
                              setWhatsappIds(selected);
                              setWhatsappId(selected && selected.length > 0 ? selected[0] : null);
                            }}
                            disabled={!campaignEditable}
                            renderValue={(selected) => {
                              if (!selected || selected.length === 0) return "Selecione ao menos um WhatsApp";
                              if (selected.length === 1) {
                                const w = whatsapps.find((x) => x.id === selected[0]);
                                return w ? w.name : `WhatsApp ${selected[0]}`;
                              }
                              return `${selected.length} WhatsApps selecionados`;
                            }}
                          >
                            {whatsapps.map((whatsapp) => {
                              const isConnected = whatsapp.status === "CONNECTED";
                              const sel = whatsappIds.length > 0 ? whatsappIds : (whatsappId ? [whatsappId] : []);
                              const checked = sel.indexOf(whatsapp.id) > -1;
                              return (
                                <MenuItem key={whatsapp.id} value={whatsapp.id}>
                                  <Checkbox checked={checked} />
                                  <Box display="flex" alignItems="center" flex={1}>
                                    <span
                                      style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        backgroundColor: isConnected ? "#4caf50" : "#f44336",
                                        display: "inline-block",
                                        marginRight: 8,
                                      }}
                                    />
                                    {whatsapp.name}
                                    <Typography variant="caption" style={{ marginLeft: 8, color: isConnected ? "#4caf50" : "#f44336", fontWeight: "bold" }}>
                                      {isConnected ? "Conectado" : "Desconectado"}
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </Select>
                          <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: "block" }}>
                            Selecione um ou mais WhatsApps. Com vários, o sistema escolhe aleatoriamente a cada envio.
                          </Typography>
                        </FormControl>
                      </Grid>

                      {/* Agendamento */}
                      <Grid xs={12} md={6} item>
                        <Field
                          as={TextField}
                          label="Agendar para"
                          name="scheduledAt"
                          error={touched.scheduledAt && Boolean(errors.scheduledAt)}
                          helperText={touched.scheduledAt && errors.scheduledAt}
                          variant="outlined"
                          margin="dense"
                          type="datetime-local"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          disabled={!campaignEditable}
                        />
                      </Grid>

                      {/* Abrir Ticket */}
                      <Grid xs={12} md={3} item>
                        <FormControl
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        >
                          <InputLabel id="openTicket-selection-label">
                            {i18n.t("campaigns.dialog.form.openTicket")}
                          </InputLabel>
                          <Field
                            as={Select}
                            label={i18n.t("campaigns.dialog.form.openTicket")}
                            labelId="openTicket-selection-label"
                            id="openTicket"
                            name="openTicket"
                            error={touched.openTicket && Boolean(errors.openTicket)}
                            disabled={!campaignEditable}
                          >
                            <MenuItem value="disabled">Desabilitado</MenuItem>
                            <MenuItem value="enabled">Habilitado</MenuItem>
                          </Field>
                        </FormControl>
                      </Grid>

                      {/* Selecionar Usuário */}
                      <Grid xs={12} md={3} item>
                        <Autocomplete
                          style={{ marginTop: '8px' }}
                          variant="outlined"
                          margin="dense"
                          getOptionLabel={(option) => `${option.name}`}
                          value={selectedUser}
                          size="small"
                          onChange={(e, newValue) => {
                            setSelectedUser(newValue);
                            if (newValue != null && Array.isArray(newValue.queues)) {
                              if (newValue.queues.length === 1) {
                                setSelectedQueue(newValue.queues[0].id);
                              }
                              setQueues(newValue.queues);
                            } else {
                              setQueues(allQueues);
                              setSelectedQueue("");
                            }
                          }}
                          options={options}
                          filterOptions={filterOptions}
                          freeSolo
                          fullWidth
                          autoHighlight
                          disabled={!campaignEditable || values.openTicket === 'disabled'}
                          noOptionsText={i18n.t("transferTicketModal.noOptions")}
                          loading={loading}
                          renderOption={(option) => (
                            <span>
                              <UserStatusIcon user={option} /> {option.name}
                            </span>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={i18n.t("transferTicketModal.fieldLabel")}
                              variant="outlined"
                              onChange={(e) => setSearchParam(e.target.value)}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <React.Fragment>
                                    {loading ? (
                                      <CircularProgress color="inherit" size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </React.Fragment>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>

                      {/* Transferir para Fila */}
                      <Grid xs={12} md={3} item>
                        <FormControl
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        >
                          <InputLabel>
                            {i18n.t("transferTicketModal.fieldQueueLabel")}
                          </InputLabel>
                          <Select
                            value={selectedQueue || ""}
                            onChange={(e) => setSelectedQueue(e.target.value)}
                            label={i18n.t("transferTicketModal.fieldQueueLabel")}
                            required={!isNil(selectedUser)}
                            disabled={!campaignEditable || values.openTicket === 'disabled'}
                          >
                            <MenuItem value="">Selecione...</MenuItem>
                            {queues.map((queue) => (
                              <MenuItem key={queue.id} value={queue.id}>
                                {queue.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Status do Ticket */}
                      <Grid xs={12} md={3} item>
                        <FormControl
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          disabled={!campaignEditable || values.openTicket === 'disabled'}
                        >
                          <InputLabel id="statusTicket-selection-label">
                            {i18n.t("campaigns.dialog.form.statusTicket")}
                          </InputLabel>
                          <Field
                            as={Select}
                            label={i18n.t("campaigns.dialog.form.statusTicket")}
                            labelId="statusTicket-selection-label"
                            id="statusTicket"
                            name="statusTicket"
                            error={touched.statusTicket && Boolean(errors.statusTicket)}
                            disabled={!campaignEditable || values.openTicket === 'disabled'}
                          >
                            <MenuItem value="closed">{i18n.t("campaigns.dialog.form.closedTicketStatus")}</MenuItem>
                            <MenuItem value="pending">{i18n.t("campaigns.dialog.form.pendingTicketStatus")}</MenuItem>
                            <MenuItem value="open">{i18n.t("campaigns.dialog.form.openTicketStatus")}</MenuItem>
                          </Field>
                        </FormControl>
                      </Grid>

                      {/* Campanha Fantasma: apaga conteúdo enviado no sistema após o envio */}
                      <Grid xs={12} md={6} item>
                        <FormControlLabel
                          control={
                            <Field
                              as={Switch}
                              type="checkbox"
                              name="ghostMode"
                              color="primary"
                              disabled={!campaignEditable || values.openTicket === "disabled"}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Campanha Fantasma — apaga o conteúdo enviado no sistema após o envio (o contato já recebeu no WhatsApp)
                            </Typography>
                          }
                        />
                      </Grid>

                      {/* Envio de Botões ou Lista (após texto e mídias) */}
                      <Grid xs={12} item>
                        <FormControlLabel
                          control={
                            <Field
                              as={Switch}
                              type="checkbox"
                              name="sendInteractive"
                              color="primary"
                              disabled={!campaignEditable || values.openTicket === "disabled"}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Envio de Botões ou Lista — envia mensagem interativa (botões ou lista) após o texto e os arquivos. Requer &quot;Abrir ticket&quot; habilitado.
                            </Typography>
                          }
                        />
                      </Grid>

                      {values.sendInteractive && (
                        <>
                          <Grid xs={12} item>
                            <Typography variant="subtitle2" gutterBottom>Tipo de mensagem interativa</Typography>
                            <RadioGroup row value={values.interactiveType || "button"} onChange={(e) => setFieldValue("interactiveType", e.target.value)}>
                              <FormControlLabel value="button" control={<Radio />} label="Botões (até 3)" disabled={!campaignEditable} />
                              <FormControlLabel value="list" control={<Radio />} label="Lista" disabled={!campaignEditable} />
                            </RadioGroup>
                          </Grid>
                          <Grid xs={12} item>
                            <Field
                              as={TextField}
                              name="interactiveBodyText"
                              label="Texto da mensagem (botões/lista)"
                              fullWidth
                              size="small"
                              variant="outlined"
                              multiline
                              rows={2}
                              placeholder="Ex: Escolha uma opção:"
                              disabled={!campaignEditable}
                            />
                          </Grid>
                          {values.interactiveType === "list" && (
                            <Grid xs={12} item>
                              <Field
                                as={TextField}
                                name="interactiveButtonText"
                                label="Texto do botão que abre a lista"
                                fullWidth
                                size="small"
                                variant="outlined"
                                placeholder="Ex: Ver opções"
                                disabled={!campaignEditable}
                              />
                            </Grid>
                          )}
                          {values.interactiveType === "button" && (
                            <Grid xs={12} item>
                              <Paper variant="outlined" style={{ padding: 12 }}>
                                <Typography variant="subtitle2" gutterBottom>Botões (máx. 3, título até 20 caracteres)</Typography>
                                {(values.interactiveButtons || []).map((btn, i) => (
                                  <Box key={i} display="flex" alignItems="center" gap={1} mb={1}>
                                    <TextField
                                      size="small"
                                      label="ID"
                                      value={btn.id}
                                      onChange={(e) => {
                                        const next = [...(values.interactiveButtons || [])];
                                        next[i] = { ...next[i], id: e.target.value };
                                        setFieldValue("interactiveButtons", next);
                                      }}
                                      style={{ width: 120 }}
                                      disabled={!campaignEditable}
                                    />
                                    <TextField
                                      size="small"
                                      label="Título"
                                      value={btn.title}
                                      onChange={(e) => {
                                        const next = [...(values.interactiveButtons || [])];
                                        next[i] = { ...next[i], title: e.target.value };
                                        setFieldValue("interactiveButtons", next);
                                      }}
                                      style={{ flex: 1 }}
                                      disabled={!campaignEditable}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => setFieldValue("interactiveButtons", (values.interactiveButtons || []).filter((_, idx) => idx !== i))}
                                      disabled={!campaignEditable || (values.interactiveButtons || []).length <= 1}
                                    >
                                      <RemoveCircleOutline />
                                    </IconButton>
                                  </Box>
                                ))}
                                {(values.interactiveButtons || []).length < 3 && campaignEditable && (
                                  <Button
                                    size="small"
                                    startIcon={<AddCircleOutline />}
                                    onClick={() => setFieldValue("interactiveButtons", [...(values.interactiveButtons || []), { id: `opt${(values.interactiveButtons || []).length + 1}`, title: `Opção ${(values.interactiveButtons || []).length + 1}` }])}
                                  >
                                    Adicionar botão
                                  </Button>
                                )}
                              </Paper>
                            </Grid>
                          )}
                          {values.interactiveType === "list" && (
                            <Grid xs={12} item>
                              <Paper variant="outlined" style={{ padding: 12 }}>
                                <Typography variant="subtitle2" gutterBottom>Seções e itens da lista</Typography>
                                {(values.interactiveSections || []).map((sec, si) => (
                                  <Box key={si} mb={2}>
                                    <TextField
                                      size="small"
                                      label="Título da seção"
                                      value={sec.title}
                                      onChange={(e) => {
                                        const next = [...(values.interactiveSections || [])];
                                        next[si] = { ...next[si], title: e.target.value };
                                        setFieldValue("interactiveSections", next);
                                      }}
                                      fullWidth
                                      className={classes.field}
                                      disabled={!campaignEditable}
                                    />
                                    {(sec.rows || []).map((row, ri) => (
                                      <Box key={ri} display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                                        <TextField size="small" label="ID" value={row.id} onChange={(e) => {
                                          const next = [...(values.interactiveSections || [])];
                                          next[si].rows[ri] = { ...next[si].rows[ri], id: e.target.value };
                                          setFieldValue("interactiveSections", next);
                                        }} style={{ width: 100 }} disabled={!campaignEditable} />
                                        <TextField size="small" label="Título" value={row.title} onChange={(e) => {
                                          const next = [...(values.interactiveSections || [])];
                                          next[si].rows[ri] = { ...next[si].rows[ri], title: e.target.value };
                                          setFieldValue("interactiveSections", next);
                                        }} style={{ width: 160 }} disabled={!campaignEditable} />
                                        <TextField size="small" label="Descrição (opcional)" value={row.description || ""} onChange={(e) => {
                                          const next = [...(values.interactiveSections || [])];
                                          next[si].rows[ri] = { ...next[si].rows[ri], description: e.target.value };
                                          setFieldValue("interactiveSections", next);
                                        }} style={{ flex: 1, minWidth: 120 }} disabled={!campaignEditable} />
                                        <IconButton size="small" onClick={() => {
                                          const next = [...(values.interactiveSections || [])];
                                          if (next[si].rows.length <= 1) return;
                                          next[si].rows = next[si].rows.filter((_, i) => i !== ri);
                                          setFieldValue("interactiveSections", next);
                                        }} disabled={!campaignEditable || (sec.rows || []).length <= 1}>
                                          <RemoveCircleOutline />
                                        </IconButton>
                                      </Box>
                                    ))}
                                    <Button size="small" startIcon={<AddCircleOutline />} onClick={() => {
                                      const next = [...(values.interactiveSections || [])];
                                      next[si].rows = [...(next[si].rows || []), { id: `r${Date.now()}`, title: "Novo item", description: "" }];
                                      setFieldValue("interactiveSections", next);
                                    }} disabled={!campaignEditable}>
                                      Adicionar item
                                    </Button>
                                  </Box>
                                ))}
                              </Paper>
                            </Grid>
                          )}
                        </>
                      )}

                      {/* Tabs de Mensagens */}
                      <Grid xs={12} item>
                        {errors.atLeastOneMessage && (
                          <Typography color="error" variant="caption" style={{ display: "block", marginBottom: 8 }}>
                            {errors.atLeastOneMessage}
                          </Typography>
                        )}
                        <Tabs
                          value={messageTab}
                          indicatorColor="primary"
                          textColor="primary"
                          onChange={(e, v) => setMessageTab(v)}
                          variant="fullWidth"
                          style={{
                            background: "#f2f2f2",
                            border: "1px solid #e6e6e6",
                            borderRadius: 4,
                          }}
                        >
                          <Tab label="Msg. 1" />
                          <Tab label="Msg. 2" />
                          <Tab label="Msg. 3" />
                          <Tab label="Msg. 4" />
                          <Tab label="Msg. 5" />
                        </Tabs>

                        <Box style={{ paddingTop: 16 }}>
                          {messageTab === 0 && renderMessageField("message1", setFieldValue)}
                          {messageTab === 1 && renderMessageField("message2", setFieldValue)}
                          {messageTab === 2 && renderMessageField("message3", setFieldValue)}
                          {messageTab === 3 && renderMessageField("message4", setFieldValue)}
                          {messageTab === 4 && renderMessageField("message5", setFieldValue)}
                        </Box>
                      </Grid>

                      {/* Anexos - Múltiplos Arquivos */}
                      <Grid xs={12} item>
                        <Typography variant="subtitle2" style={{ marginBottom: 8, color: "#666" }}>
                          Arquivos (serão enviados em sequência: Texto → Arquivo 1 → Arquivo 2 → Arquivo 3)
                        </Typography>
                        <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                          {/* Arquivo 1 */}
                          {!attachment && !campaign.mediaPath && campaignEditable && (
                            <Button
                              color="primary"
                              onClick={() => attachmentFile.current.click()}
                              variant="outlined"
                              startIcon={<AttachFileIcon />}
                              size="small"
                            >
                              Arquivo 1
                            </Button>
                          )}
                          {(campaign.mediaPath || attachment) && (
                            <Box display="flex" alignItems="center">
                              <Chip
                                icon={<AttachFileIcon />}
                                label={`1: ${attachment ? attachment.name : campaign.mediaName}`}
                                color="primary"
                                variant="outlined"
                                size="small"
                              />
                              {campaignEditable && (
                                <IconButton 
                                  onClick={() => handleDeleteMediaClick(1)} 
                                  color="secondary"
                                  size="small"
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          )}

                          {/* Arquivo 2 */}
                          {!attachment2 && !campaign.mediaPath2 && campaignEditable && (
                            <Button
                              color="primary"
                              onClick={() => attachmentFile2.current.click()}
                              variant="outlined"
                              startIcon={<AttachFileIcon />}
                              size="small"
                            >
                              Arquivo 2
                            </Button>
                          )}
                          {(campaign.mediaPath2 || attachment2) && (
                            <Box display="flex" alignItems="center">
                              <Chip
                                icon={<AttachFileIcon />}
                                label={`2: ${attachment2 ? attachment2.name : campaign.mediaName2}`}
                                color="secondary"
                                variant="outlined"
                                size="small"
                              />
                              {campaignEditable && (
                                <IconButton 
                                  onClick={() => handleDeleteMediaClick(2)} 
                                  color="secondary"
                                  size="small"
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          )}

                          {/* Arquivo 3 */}
                          {!attachment3 && !campaign.mediaPath3 && campaignEditable && (
                            <Button
                              color="default"
                              onClick={() => attachmentFile3.current.click()}
                              variant="outlined"
                              startIcon={<AttachFileIcon />}
                              size="small"
                            >
                              Arquivo 3
                            </Button>
                          )}
                          {(campaign.mediaPath3 || attachment3) && (
                            <Box display="flex" alignItems="center">
                              <Chip
                                icon={<AttachFileIcon />}
                                label={`3: ${attachment3 ? attachment3.name : campaign.mediaName3}`}
                                color="default"
                                variant="outlined"
                                size="small"
                              />
                              {campaignEditable && (
                                <IconButton 
                                  onClick={() => handleDeleteMediaClick(3)} 
                                  color="secondary"
                                  size="small"
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          )}
                        </Box>

                        {/* Inputs ocultos para upload */}
                        <input
                          type="file"
                          ref={attachmentFile2}
                          style={{ display: "none" }}
                          onChange={(e) => {
                            if (e.target.files[0]) setAttachment2(e.target.files[0]);
                          }}
                        />
                        <input
                          type="file"
                          ref={attachmentFile3}
                          style={{ display: "none" }}
                          onChange={(e) => {
                            if (e.target.files[0]) setAttachment3(e.target.files[0]);
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Coluna do Preview do Celular */}
                  <Grid item xs={12} md={4}>
                    <PhonePreview
                      message={getCurrentMessage(values)}
                      attachment={attachment}
                      mediaPath={campaign.mediaPath}
                      mediaName={campaign.mediaName}
                      attachment2={attachment2}
                      mediaPath2={campaign.mediaPath2}
                      mediaName2={campaign.mediaName2}
                      attachment3={attachment3}
                      mediaPath3={campaign.mediaPath3}
                      mediaName3={campaign.mediaName3}
                      sendInteractive={values.sendInteractive}
                      interactiveType={values.interactiveType}
                      interactiveBodyText={values.interactiveBodyText}
                      interactiveButtonText={values.interactiveButtonText}
                      interactiveButtons={values.interactiveButtons}
                      interactiveSections={values.interactiveSections}
                    />
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                {campaign.status === "CANCELADA" && (
                  <Button color="primary" onClick={restartCampaign} variant="outlined">
                    Reiniciar
                  </Button>
                )}
                {campaign.status === "EM_ANDAMENTO" && (
                  <Button color="secondary" onClick={cancelCampaign} variant="outlined">
                    Cancelar Campanha
                  </Button>
                )}
                <Button onClick={handleClose} color="primary" variant="outlined">
                  Fechar
                </Button>
                {(campaignEditable || campaign.status === "CANCELADA") && (
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                    variant="contained"
                    className={classes.btnWrapper}
                  >
                    {campaignId ? "Salvar" : "Criar Campanha"}
                    {isSubmitting && (
                      <CircularProgress size={24} className={classes.buttonProgress} />
                    )}
                  </Button>
                )}
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default CampaignProModal;
